import { Injectable, signal, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Buffer } from "buffer";
import { environment } from '../../environments/environment';

export interface PageTemplate {
  template: string,
  hasDropDownMenu: boolean,
  backgroundImage?: string
  header?: {
    title: string,
    subtitle?: string,
    lines?: [string]
  },
  body?: {
    bodyText: string,
    context?: [
      title: string,
      subTitle?: string,
      icon?: string,
      image?: string,
      body?: string,
      moreBody?: string,
      button?: {
        text: string,
        urlPath: string,
      },
      context?: any
    ]
  },
  footer?: {
    title: string,
    subtitle?: string,
    lines?: [string]
  }
}

export interface DropDownMenu {
  icon?: string,
  title: string,
  urlPath: string
}

export interface SiteTemplateV4 {
  dropDownMenu?: Array<DropDownMenu>,
  info?: {
    siteTitle: string,
    siteIcon: string,
    backendVersion: string,
    frontendVersion: string,
    copyRight: string
  },
  pages?: {
    [key: string]: PageTemplate
  }
}


@Injectable({
  providedIn: 'root'
})
export class BackendV4Service {

  private wwwTemplate
  private fqdn: string = ''
  private requesterIP: string = ''
  private backEndVersion = ''
  private loginCredentials
  private userData
  private courseList
  private enrollAgreement

  readonly ONE_MINUTE = (1 * 60 * 1000)
  readonly ONE_HOUR = (60 * this.ONE_MINUTE)
  readonly ONE_DAY = (24 * this.ONE_HOUR)
  readonly ONE_WEEK = (7 * this.ONE_DAY)

  constructor(private httpClient: HttpClient) {
    this.wwwTemplate = signal<SiteTemplateV4 | null>({})
    this.loginCredentials = signal<{ authKey: string | null, refreshKey: string | null, rbac: Array<string> | null, error?: string | null } | null>({ authKey: null, refreshKey: null, rbac: [] })
    this.userData = signal<{} | null>(null)
    this.courseList = signal<[] | null>(null)
    this.enrollAgreement = signal<[] | null>(null)

    const templateData = localStorage.getItem('templateData')
    if (templateData) {
      let template = JSON.parse(templateData)
      if (template && template.timeStamp) {
        const now = new Date().getTime()
        const timeStamp = new Date(template.timeStamp).getTime()
        if (timeStamp + this.ONE_HOUR < now) {
          localStorage.removeItem(templateData)
        } else {
          this.updateWwwTemplateAPI(template)
        }
      }
    }

    if (this.fqdn.length === 0) {
      this.httpClient.get(environment.apiUrl).subscribe((response: any) => {
        response['timeStamp'] = new Date()
        localStorage.setItem('templateData', JSON.stringify(response))
        this.updateWwwTemplateAPI(response)
      })
    }
  }

  private updateWwwTemplateAPI(template: any) {
    const templateData = JSON.parse(Buffer.from(template.data, "base64").toString('utf8'))
    if (!templateData.info) {
      templateData['info'] = {}
    }
    if (templateData.info) {
      templateData.info.frontendVersion = "3452"
      templateData.info.backendVersion = template.backEndVersion
    }
    this.wwwTemplate.set(templateData)
    this.fqdn = template.fqdn
    this.requesterIP = template.requester
    this.backEndVersion = template.backEndMinorVersion
  }

  getWwwTemplate(): Signal<SiteTemplateV4 | null> {
    return this.wwwTemplate;
  }

  public getFQDN() {
    return this.fqdn
  }
  public getRequesterIp() {
    return this.requesterIP
  }
  public getBackEndVersion() {
    return this.backEndVersion
  }

  public getOneTimePasswordAPI(username: string) {
    const post = {
      subAPI: 'request',
      data: {
        username: username
      }
    }
    this.httpClient.post(environment.rbacUrl, post).subscribe((response: any) => {
      console.log('OPT Request submitted')
    })
  }

  public loginWithOneTimePasswordAPI(username: string, otp: string) {

    const post = {
      subAPI: 'validate',
      data: {
        username: username,
        otp: otp
      }
    }
    localStorage.removeItem('authentication')
    this.loginCredentials.set({ authKey: null, refreshKey: null, rbac: [], error: null })

    this.httpClient.post(environment.rbacUrl, post).subscribe((response: any) => {
      let data: any = {}
      let auth: any = { authKey: null, refreshKey: null, rbac: [] }
      if (response.data) {
        response['timeStamp'] = new Date()
        localStorage.setItem('authentication', JSON.stringify(response))
        data = JSON.parse(Buffer.from(response.data, "base64").toString('utf8'))
        auth = data.auth
        console.log(auth)
        if (data.userData) {
          response['timeStamp'] = new Date()
          localStorage.setItem('userData', JSON.stringify(response))
          this.userData.set(data.userData)
        } else {
          localStorage.removeItem('userData')
          this.userData.set({})
        }
        this.loginCredentials.set({ authKey: auth.authKey, refreshKey: auth.refreshKey, rbac: auth.rbac, error: null })
      }
      if (response.error) {
        localStorage.removeItem('authentication')
        localStorage.removeItem('userData')
        this.userData.set(null)
        this.loginCredentials.set({ authKey: null, refreshKey: null, rbac: [], error: response.error })
      }
    })
  }

  getLoginCredentials(): Signal<{ authKey: string | null, refreshKey: string | null, rbac: Array<string> | null, error?: string | null } | null> {
    return this.loginCredentials;
  }

  newUserAPI(userInfo: any) {
    const post = {
      subAPI: 'create',
      data: {
        ...userInfo
      },
      authentication: this.loginCredentials()
    }
    this.httpClient.post(environment.apiUrl + "user", post).subscribe((response: any) => {
      let data = null
      if (response.data) {
        response['timeStamp'] = new Date()
        localStorage.setItem('userData', JSON.stringify(response))
        data = JSON.parse(Buffer.from(response.data, "base64").toString('utf8'))
        if (data.userDate) {
          this.userData.set(data.userData)
        }
      }

    })
  }

  updateUserAPI(userInfo: any) {
    const post = {
      subAPI: 'update',
      data: {
        userId: userInfo.userId,
        update: { ...userInfo }
      },
      authentication: this.loginCredentials()
    }
    this.httpClient.post(environment.apiUrl + "user", post).subscribe((response: any) => {
      let userData = null
      if (response.data) {
        response['timeStamp'] = new Date()
        localStorage.setItem('userData', JSON.stringify(response))
        userData = JSON.parse(Buffer.from(response.data, "base64").toString('utf8'))
      }
      this.userData.set(userData)
    })
  }

  getUserData(): Signal<any | null> {
    return this.userData;
  }

  postQuestion(questionForm: any) {
    const post = {
      subAPI: 'postQuestion',
      data: {
        questions: questionForm.questions,
        name: questionForm.name,
        emailAddress: questionForm.email
      }
    }
    this.httpClient.post(environment.apiUrl, post).subscribe((response: any) => {
      let data = null
      if (response.data) {
        response['timeStamp'] = new Date()
        data = JSON.parse(Buffer.from(response.data, "base64").toString('utf8'))
        console.log(data)
      }

    })
  }
}
